import { Form } from 'antd';
import { useId } from 'react';
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type RegisterOptions
} from 'react-hook-form';

interface IProps<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>
> {
  /** Форма, к которой относится поле; по нему выводятся типы `name`, `rules` и `field`. */
  control: Control<TValues>;
  name: TName;
  label: string;
  required?: boolean;
  /** Подсказка под полем; при ошибке её место занимает текст ошибки. */
  hint?: string;
  rules?: RegisterOptions<TValues, TName>;
  /** Рисует контрол: `field` передаётся в него целиком, `id` связывает его с подписью. */
  render: (
    field: ControllerRenderProps<TValues, TName> & { id: string }
  ) => React.ReactNode;
}

/**
 * Поле формы: Controller react-hook-form + разметка antd `Form.Item` (подпись,
 * отметка обязательности, подсказка или ошибка). Значение и валидация — только в RHF,
 * `Form.Item` не получает `name` и `rules`.
 */
const FormField = <
  TValues extends FieldValues,
  TName extends FieldPath<TValues>
>({
  control,
  name,
  label,
  required,
  hint,
  rules,
  render
}: IProps<TValues, TName>): React.ReactElement => {
  const id = useId();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <Form.Item
          label={label}
          htmlFor={id}
          required={required}
          validateStatus={error ? 'error' : ''}
          help={error?.message ?? hint}
        >
          {render({ ...field, id })}
        </Form.Item>
      )}
    />
  );
};

export default FormField;
